import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SettingsService } from '../../core/services/settings.service';
import { Location, Branch } from '../../core/models/settings.models';
import { confirmAction } from '../../core/utils/confirm.util';

@Component({
  selector: 'app-locations',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './locations.component.html',
  styleUrls: ['../../settings-shared.css', './locations.component.css'],
})
export class LocationsComponent implements OnInit {
  private readonly settings = inject(SettingsService);

  protected readonly showModal = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly isEditing = computed(() => this.editingId() !== null);
  protected readonly showToast = signal(false);
  protected readonly toastMessage = signal('');

  protected locationForm: Omit<Location, 'id'> = {
    code: '',
    name: '',
    branchId: '',
    type: 'warehouse',
    status: 'active',
  };

  protected readonly locations = this.settings.scopedLocations;
  protected readonly branches = this.settings.scopedBranches;

  protected readonly branchNameById = computed(() => {
    const map = new Map<string, string>();
    for (const branch of this.branches()) {
      map.set(branch.id, branch.name);
    }
    return map;
  });

  ngOnInit(): void {
    this.settings.refreshFromApi();
  }

  openModal(): void {
    this.editingId.set(null);
    this.resetForm();
    this.showModal.set(true);
  }

  openEdit(row: Location): void {
    this.editingId.set(row.id);
    this.locationForm = {
      code: row.code,
      name: row.name,
      branchId: row.branchId,
      type: row.type,
      status: row.status,
    };
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingId.set(null);
  }

  getBranchName(branchId: string): string {
    return this.branchNameById().get(branchId) ?? '—';
  }

  save(): void {
    const editingId = this.editingId();

    if (!this.locationForm.code || !this.locationForm.name) {
      this.toastMessage.set('Code and Name are required');
      this.showToast.set(true);
      setTimeout(() => this.showToast.set(false), 4000);
      return;
    }

    if (!this.locationForm.branchId) {
      this.toastMessage.set('Please select a branch');
      this.showToast.set(true);
      setTimeout(() => this.showToast.set(false), 4000);
      return;
    }

    if (editingId) {
      if (!this.settings.updateLocation(editingId, this.locationForm)) {
        this.toastMessage.set(
          'Location update is not available via the API yet.',
        );
        this.showToast.set(true);
        setTimeout(() => this.showToast.set(false), 4000);
        return;
      }
      this.toastMessage.set('Location updated successfully');
    } else {
      this.settings.addLocation(this.locationForm);
      this.toastMessage.set('Location added successfully');
    }

    this.closeModal();
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  deleteRow(row: Location): void {
    if (!confirmAction(`Delete "${row.name}"? This cannot be undone.`)) {
      return;
    }

    if (!this.settings.deleteLocation(row.id)) {
      this.toastMessage.set(
        'Location delete is not available via the API yet.',
      );
    } else {
      this.toastMessage.set('Location deleted successfully');
    }
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  private resetForm(): void {
    this.locationForm = {
      code: '',
      name: '',
      branchId: '',
      type: 'warehouse',
      status: 'active',
    };
  }
  getTypeIcon(type: string): string {
    const icons: Record<string, string> = {
      warehouse: 'bi-building',
      store: 'bi-shop',
      garage: 'bi-truck',
      office: 'bi-briefcase',
    };
    return icons[type] || 'bi-building';
  }
}
